import React from 'react';
import map from 'lodash/map';

import { ClassName, GADataType, I18nText, UUID } from '../../../../../types';
import {
  SmartContractShareIteration,
  SmartContractShareStatus,
  SmartContractShareStatuses,
  UpdateSmartContractSharesCacheKeys
} from '../../../smartContractSharesTypes';
import { TaskID } from '../../../../tasks/tasksTypes';
import { UserClient, UserID } from '../../../../users/usersTypes';
import { CompanyEventTemplateID } from '../../../../companyEventTemplates/companyEventTemplatesTypes';

import { IconsEnum } from '../../../../../assets/icons/types';
import { CreateSmartContractShareFormView } from '../../forms/CreateSmartContractShareForm/CreateSmartContractShareForm.types';

import { useBatchCreateSmartContractSharesForm } from '../../forms/CreateSmartContractShareForm/hooks/useBatchCreateSmartContractSharesForm';
import { CreateSmartContractShareForm } from '../../forms/CreateSmartContractShareForm';

import { AlertMessage } from '../../../../../helpers/AlertMessage';
import { SimpleModalButton } from '../../../../../helpers/buttons/SimpleModalButton';

import { usersKeys, words } from '../../../../../locales/keys';

interface BatchCreateSmartContractSharesModalButtonProps {
  invitedUserUuids?: UUID[];
  iteration?: SmartContractShareIteration;
  tasks: { id: TaskID }[];
  status?: SmartContractShareStatus;
  className?: ClassName;
  icon?: IconsEnum;
  iconClassName?: ClassName;
  i18nText?: I18nText;
  i18nTextClassName?: ClassName;
  i18nTitle?: I18nText;
  tooltipI18nText?: I18nText;
  cacheKeys?: UpdateSmartContractSharesCacheKeys;
  defaultUsers?: {
    id: UserID;
    client?: UserClient;
  }[];
  disabled?: boolean;
  afterCreate?: () => void;
  withoutEventIds?: CompanyEventTemplateID[];
}

const CREATE_SMART_CONTRACT_SHARE_FORM = 'create-smart-contract-share-form';

function BatchCreateSmartContractSharesModalButton({
  afterCreate,
  cacheKeys,
  className,
  dataGa,
  defaultUsers = [],
  disabled,
  i18nText,
  i18nTextClassName,
  i18nTitle = usersKeys.invite,
  icon,
  iconClassName,
  invitedUserUuids,
  iteration,
  status = SmartContractShareStatuses.CHECKED_IN,
  tasks,
  tooltipI18nText,
  withoutEventIds
}: BatchCreateSmartContractSharesModalButtonProps & GADataType) {
  const {
    control,
    batchCreateSmartContractSharesErrorMessage,
    batchCreateSmartContractSharesLoading,
    handleSubmit,
    inviteUsersToTasksErrorMessage,
    inviteUsersToTasksLoading,
    handleResetBatchCreateSmartContractSharesForm,
    setValue,
    formView,
    goToSelectUsersView,
    disabledSubmit,
    oneFormView,
    getValues,
    withoutEditUsers
  } = useBatchCreateSmartContractSharesForm({
    iteration,
    taskIds: map(tasks, 'id'),
    status,
    cacheKeys,
    defaultUsers,
    afterCreate,
    withoutEventIds
  });

  return (
    <SimpleModalButton
      className={
        className ||
        'flex text-left dark:hover:bg-gray-800 hover:bg-gray-100 px-4 py-2 text-sm w-full whitespace-nowrap'
      }
      dataGa={dataGa}
      i18nText={i18nText}
      i18nTextClassName={i18nTextClassName}
      icon={icon}
      iconClassName={iconClassName}
      i18nSubmitText={
        formView === CreateSmartContractShareFormView.CHANGE_SHARE ||
        oneFormView
          ? words.submit
          : words.next
      }
      i18nCancelText={
        formView === CreateSmartContractShareFormView.CHANGE_SHARE &&
        !oneFormView
          ? words.back
          : words.cancel
      }
      i18nTitle={
        formView === CreateSmartContractShareFormView.SELECT_USERS && i18nTitle
      }
      isLoading={
        batchCreateSmartContractSharesLoading || inviteUsersToTasksLoading
      }
      tooltipI18nText={tooltipI18nText}
      onOpen={handleResetBatchCreateSmartContractSharesForm}
      closeOnCancel={
        formView === CreateSmartContractShareFormView.SELECT_USERS ||
        oneFormView
      }
      onCancel={
        formView === CreateSmartContractShareFormView.CHANGE_SHARE &&
        !oneFormView
          ? goToSelectUsersView
          : undefined
      }
      cancelButtonPosition={
        formView === CreateSmartContractShareFormView.CHANGE_SHARE &&
        !oneFormView
          ? 'start'
          : 'end'
      }
      submitDisabled={disabledSubmit}
      onSubmit={handleSubmit}
      notHideOnSubmit={
        formView === CreateSmartContractShareFormView.SELECT_USERS &&
        !oneFormView
      }
      childrenClassName="overflow-y-auto relative"
      disabled={disabled}
    >
      <CreateSmartContractShareForm
        form={CREATE_SMART_CONTRACT_SHARE_FORM}
        isLoading={
          batchCreateSmartContractSharesLoading || inviteUsersToTasksLoading
        }
        control={control}
        formView={formView}
        goToSelectUsersView={
          formView === CreateSmartContractShareFormView.CHANGE_SHARE &&
          !oneFormView
            ? goToSelectUsersView
            : undefined
        }
        i18nTitle={i18nTitle}
        setValue={setValue}
        invitedUserUuids={invitedUserUuids}
        withoutEditUsers={withoutEditUsers}
        getValues={getValues}
        withSelectEvents
        status={status}
      />

      <div className="px-4">
        <AlertMessage
          message={
            batchCreateSmartContractSharesErrorMessage ||
            inviteUsersToTasksErrorMessage
          }
        />
      </div>
    </SimpleModalButton>
  );
}

export default BatchCreateSmartContractSharesModalButton;
